<template>
  <BaseModal
    :open="isVisible"
    :title="$t('auth.captchaPopupTitle')"
    size="sm"
    @close="handleCancel"
  >
    <div class="captcha-popup-content">
      <h3 class="captcha-popup-title">{{ $t('auth.captchaPopupTitle') }}</h3>
      <p class="captcha-popup-subtitle">{{ $t('auth.captchaPopupSubtitle') }}</p>

      <div class="captcha-popup-widget">
        <div :id="containerId" />
      </div>

      <template v-if="error">
        <p class="captcha-popup-error">{{ error }}</p>
        <!--
          Выход из тупика: проверка могла не подняться (сеть, блокировщик,
          отвалившийся скрипт провайдера). Без этой кнопки в окне не остаётся
          ничего, чем можно подтвердить, — только закрыть и потерять отправку.
        -->
        <button
          type="button"
          class="captcha-popup-retry"
          data-test="captcha-retry"
          @click="retryWidget"
        >
          {{ $t('auth.captchaRetry') }}
        </button>
      </template>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseModal from '@/shared/components/base/BaseModal.vue'
import { 
  getPopupVisibility, 
  completeCaptchaVerification, 
  cancelCaptchaVerification 
} from '@/shared/utils/captchaRetryService'
import { getCaptchaProvider } from '@/shared/utils/captchaConfig'
import { 
  loadRecaptcha, 
  renderRecaptchaWidget, 
  resetRecaptchaWidget 
} from '@/shared/utils/recaptcha'
import {
  loadTurnstile,
  renderTurnstileWidget,
  removeTurnstileWidget,
  executeTurnstileWidget
} from '@/shared/utils/turnstile'
import {
  executeRecaptchaWidget
} from '@/shared/utils/recaptcha'
import {
  initGeetestWidget,
  serializeGeetestResult,
  type GeetestCaptchaObject
} from '@/shared/utils/geetest'

const isVisible = getPopupVisibility()
const containerId = 'captcha-popup-widget'
const widgetId = ref<number | string | null>(null)
const geetestObj = ref<GeetestCaptchaObject | null>(null)
const error = ref('')
const provider = getCaptchaProvider()

const { t } = useI18n()

// Retry logic for GeeTest network errors
const MAX_GEETEST_RETRIES = 3
const GEETEST_RETRY_DELAY_MS = 1000
const geetestRetryCount = ref(0)

const handleCancel = () => {
  geetestRetryCount.value = 0 // Reset retries on cancel
  cancelCaptchaVerification()
}

const onCaptchaSuccess = (token: string) => {
  geetestRetryCount.value = 0 // Reset retries on success
  completeCaptchaVerification(token)
}

const retryGeetestCaptcha = async () => {
  if (!geetestObj.value) return
  
  console.log(`[GonkaGame] Retrying GeeTest captcha (attempt ${geetestRetryCount.value}/${MAX_GEETEST_RETRIES})`)
  
  // Wait before retry
  await new Promise(resolve => setTimeout(resolve, GEETEST_RETRY_DELAY_MS))
  
  // Reset and re-show
  geetestObj.value.reset()
  geetestObj.value.showCaptcha()
}

const onCaptchaError = (captchaError?: any) => {
  // Check if this is a GeeTest network error and we can retry
  const isGeetestNetworkError = provider === 'geetest' && (
    captchaError?.message?.includes('网络错误') ||  // Chinese "Network error"
    captchaError?.msg?.includes('网络错误') ||
    String(captchaError).includes('网络错误')
  )
  
  if (isGeetestNetworkError && geetestRetryCount.value < MAX_GEETEST_RETRIES) {
    geetestRetryCount.value++
    retryGeetestCaptcha()
    return // Don't show error, we're retrying
  }
  
  // Reset retry counter after max retries or non-network error
  geetestRetryCount.value = 0
  
  if (provider === 'geetest') {
    error.value = t('auth.captchaGeetestError')
  } else if (provider === 'turnstile') {
    error.value = t('auth.captchaTurnstileError')
  } else {
    error.value = t('auth.captchaError')
  }
}

const onCaptchaExpired = () => {
  error.value = t('auth.captchaExpired')
}

/**
 * Номер текущей сессии проверки. Растёт на каждом снятии виджета, и всё, что
 * прилетает от прежнего экземпляра провайдера (отложенный успех, ошибка,
 * догнавший колбэк), после этого игнорируется: иначе один уже потраченный
 * ответ закрыл бы следующую проверку, а это дыра в защите от перебора.
 */
let session = 0

/**
 * Снимает виджет прошлой проверки целиком: объект провайдера, его токен и его
 * обработчики.
 *
 * Зачем это вообще нужно. Окно живёт в `BaseModal`, а тот рисует содержимое
 * через `v-if` — при закрытии карточка вместе с контейнером виджета уезжает из
 * DOM. Объект провайдера при этом остаётся в памяти, но рисовать ему уже некуда:
 * его `reset()` ничего не показывает. Раньше следующий показ как раз и пытался
 * оживить такой объект — человек, отправивший вторую ссылку, видел окно с
 * заголовком и пустым телом, подтвердить в нём было нечем.
 */
const disposeWidget = () => {
  session += 1
  geetestRetryCount.value = 0

  if (geetestObj.value) {
    try {
      geetestObj.value.destroy()
    } catch {
      // Объект мог уехать вместе с разметкой — гасить нечего.
    }
    geetestObj.value = null
  }

  if (widgetId.value !== null) {
    if (provider === 'turnstile') {
      removeTurnstileWidget(widgetId.value as string)
    } else {
      // У grecaptcha нет remove(); reset() — самое сильное, что она умеет,
      // и он гасит прежний ответ.
      resetRecaptchaWidget(widgetId.value as number)
    }
    widgetId.value = null
  }
}

const initWidget = async () => {
  // Поколение этой попытки: пока идёт загрузка скрипта, человек успевает
  // закрыть окно и отправить форму заново — второй виджет не должен получить
  // в спину результат первого.
  const mySession = session
  const isStale = () => mySession !== session

  const handleSuccess = (token: string) => {
    if (!isStale()) onCaptchaSuccess(token)
  }
  const handleError = (captchaError?: unknown) => {
    if (!isStale()) onCaptchaError(captchaError)
  }
  const handleExpired = () => {
    if (!isStale()) onCaptchaExpired()
  }

  try {
    // Small delay to ensure container is in DOM
    await new Promise(resolve => setTimeout(resolve, 100))
    if (isStale()) return

    if (provider === 'geetest') {
      const captcha = await initGeetestWidget(containerId, {
        onReady: () => {
          // Auto-show challenge immediately when ready
          if (!isStale()) geetestObj.value?.showCaptcha()
        },
        onSuccess: (result) => {
          handleSuccess(serializeGeetestResult(result))
        },
        onError: handleError,
      })

      if (isStale()) {
        try {
          captcha.destroy()
        } catch {
          // см. disposeWidget
        }
        return
      }

      geetestObj.value = captcha
    } else if (provider === 'turnstile') {
      await loadTurnstile()
      if (isStale()) return

      const id = await renderTurnstileWidget(containerId, {
        onSuccess: handleSuccess,
        onError: handleError,
        onExpired: handleExpired,
      })

      if (isStale()) {
        removeTurnstileWidget(id)
        return
      }

      widgetId.value = id
      executeTurnstileWidget(id)
    } else {
      await loadRecaptcha()
      if (isStale()) return

      const id = await renderRecaptchaWidget(containerId, {
        onSuccess: handleSuccess,
        onError: handleError,
        onExpired: handleExpired,
      })

      if (isStale()) {
        resetRecaptchaWidget(id)
        return
      }

      widgetId.value = id
      executeRecaptchaWidget(id)
    }
  } catch (err) {
    console.error('[GonkaGame] Captcha popup init error:', err)
    if (!isStale()) error.value = t('auth.captchaLoadError')
  }
}

/** Повтор после отказа: та же дорога, что и у обычного показа окна. */
const retryWidget = async () => {
  error.value = ''
  disposeWidget()
  await initWidget()
}

watch(isVisible, async (visible) => {
  if (!visible) {
    // Карточка уезжает из DOM — вместе с ней должен уйти и виджет.
    disposeWidget()
    return
  }

  error.value = ''

  // Dev mode: auto-complete captcha immediately
  if (provider === 'none') {
    onCaptchaSuccess('dev-bypass')
    return
  }

  // Каждая отправка формы — своя проверка: прежнюю снимаем и строим заново.
  disposeWidget()
  await initWidget()
})
</script>

<style scoped>
.captcha-popup-title {
  font-size: var(--ds-text-size-h-sm);
  font-weight: var(--ds-weight-semibold);
  text-align: center;
  margin-bottom: 8px;
  color: var(--hm-text-primary);
}

.captcha-popup-subtitle {
  font-size: var(--ds-text-size-body);
  color: var(--hm-text-secondary);
  text-align: center;
  margin-bottom: 24px;
}

.captcha-popup-widget {
  display: flex;
  justify-content: center;
  min-height: 78px;
}

.captcha-popup-error {
  color: var(--hm-danger);
  font-size: var(--ds-text-size-body);
  text-align: center;
  margin-top: 16px;
}

.captcha-popup-retry {
  display: block;
  margin: 12px auto 0;
  padding: 8px 16px;
  border: 1px solid var(--hm-border, currentColor);
  border-radius: var(--ds-radius-control);
  background: transparent;
  color: var(--hm-text-primary);
  font-size: var(--ds-text-size-body);
  cursor: pointer;
}
</style>
